import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { Player } from './entities/Player.js';
import { SceneManager } from './core/SceneManager.js';
import { InputManager } from './utils/InputManager.js';
import { AssetLoader } from './utils/AssetLoader.js';
import { RampObstacle } from './entities/Obstacle.js';
import { FpsCounter } from './ui/FpsCounter.js';
import * as CANNON from 'cannon';
import { Map } from './entities/Map.js';
import { StartMenu } from './ui/StartMenu.js';

class Game {
    constructor() {
        this.sceneManager = new SceneManager();
        this.inputManager = null;
        this.assetLoader = null;
        this.fpsCounter = null;
        this.lastTime = 0;
        this.player = null;
        this.cameraControls = null;
        this.cameraOffset = new THREE.Vector3(0, -4, 8); // Base camera offset that will be adjusted by speed
        this.cameraLerpFactor = 0.05;
        this.physicsWorld = null; // Add physics world
        this.map = null; // Add map property
        this.followPlayerDirection = true; // Flag to determine if camera should follow player direction
        
        // Menu and game state variables
        this.startMenu = null;
        this.gameState = 'menu'; // 'menu', 'playing', or 'paused'
        this.wasEscapePressed = false;
        
        // Player spawn position
        this.playerSpawnPosition = new THREE.Vector3(0, 5, 0);
        
        // Camera dynamic adjustment parameters
        this.minCameraY = -2;  // Starting Y value at 0 speed
        this.maxCameraY = -7;  // Maximum Y value at max speed (without downhill boost)
        this.maxCameraYWithDownhill = -15; // Maximum Y value with maximum downhill boost
        this.minCameraZ = 6;   // Starting Z value at 0 speed
        this.maxCameraZ = 10;  // Maximum Z value at max speed
        this.maxSpeedForCamera = 20; // Speed threshold for maximum camera adjustment
    }

    async init() {
        this.sceneManager.init();
        
        // Initialize input
        this.inputManager = new InputManager();
        this.inputManager.init();
        
        // Initialize asset loader
        this.assetLoader = new AssetLoader();
        
        // Initialize FPS counter
        this.fpsCounter = new FpsCounter();
        this.fpsCounter.init();
        this.fpsCounter.setVisibility(false); // Initially hidden

        // Initialize physics world
        this.physicsWorld = new CANNON.World();
        this.physicsWorld.gravity.set(0, -9.82, 0); // Set gravity

        // Create ground physics
        const groundShape = new CANNON.Plane();
        const groundBody = new CANNON.Body({ mass: 0 }); // mass: 0 for static
        groundBody.addShape(groundShape);
        groundBody.quaternion.setFromAxisAngle(new CANNON.Vec3(1, 0, 0), -Math.PI / 2); // Align with Three.js ground
        this.physicsWorld.addBody(groundBody);

        // Create visual ground (unchanged)
        const groundGeometry = new THREE.PlaneGeometry(2000, 2000, 200, 200);
        const textureSize = 512;
        const canvas = document.createElement('canvas');
        canvas.width = textureSize;
        canvas.height = textureSize;
        const context = canvas.getContext('2d');
        const darkGreen1 = '#1a4314';
        const darkGreen2 = '#245419';
        const tileSize = textureSize / 8;
        for (let x = 0; x < textureSize; x += tileSize) {
            for (let y = 0; y < textureSize; y += tileSize) {
                context.fillStyle = ((x + y) / tileSize) % 2 === 0 ? darkGreen1 : darkGreen2;
                context.fillRect(x, y, tileSize, tileSize);
            }
        }
        const groundTexture = new THREE.CanvasTexture(canvas);
        groundTexture.wrapS = THREE.RepeatWrapping;
        groundTexture.wrapT = THREE.RepeatWrapping;
        groundTexture.repeat.set(50, 50);
        const groundMaterial = new THREE.MeshStandardMaterial({ 
            map: groundTexture,
            roughness: 0.8,
            metalness: 0.2
        });
        const ground = new THREE.Mesh(groundGeometry, groundMaterial);
        ground.rotation.x = -Math.PI / 2;
        ground.receiveShadow = true;
        this.sceneManager.scene.add(ground);

        // Add fog, lights
        this.sceneManager.scene.fog = new THREE.Fog(0x87ceeb, 50, 500);
        const ambientLight = new THREE.AmbientLight(0xffffff, 0.7);
        const directionalLight = new THREE.DirectionalLight(0xffffff, 1.0);
        directionalLight.position.set(50, 100, 50);
        directionalLight.castShadow = true;
        
        // Improve shadow quality and coverage
        directionalLight.shadow.mapSize.width = 4096;  // Increased from default (512)
        directionalLight.shadow.mapSize.height = 4096; // Increased from default (512)
        directionalLight.shadow.camera.near = 0.5;
        directionalLight.shadow.camera.far = 500;      // Extend far distance
        
        // Expand the shadow camera frustum to cover a larger area
        const shadowSize = 100;                         // Much larger shadow area (default is usually 5)
        directionalLight.shadow.camera.left = -shadowSize;
        directionalLight.shadow.camera.right = shadowSize;
        directionalLight.shadow.camera.top = shadowSize;
        directionalLight.shadow.camera.bottom = -shadowSize;
        
        // Improve shadow quality
        directionalLight.shadow.bias = -0.0005;        // Reduce shadow acne
        
        this.sceneManager.scene.add(ambientLight, directionalLight);
        const backLight = new THREE.DirectionalLight(0xffffff, 0.5);
        backLight.position.set(-5, 8, -7);
        this.sceneManager.scene.add(backLight);

        // Load the map
        try {
            console.log('Loading map...');
            this.map = new Map(this.assetLoader);
            const mapLoaded = await this.map.loadMap('assets/models/TreeMap_0.2_final.glb', this.physicsWorld);
            if (!mapLoaded) {
                console.warn('Failed to load map');
            } else {
                console.log('Map loaded successfully');
                this.sceneManager.scene.add(this.map.getObject());
            }
        } catch (error) {
            console.error('Error loading map:', error);
        }

        // Create and add player
        this.player = new Player(this.assetLoader);
            
        // Initialize player spawn position from map
        if (this.map && this.map.getSpawnPosition()) {
            this.playerSpawnPosition = this.map.getSpawnPosition().clone();
            this.player.getObject().position.copy(this.playerSpawnPosition);
            console.log(`Setting player spawn position to: (${this.playerSpawnPosition.x}, ${this.playerSpawnPosition.y}, ${this.playerSpawnPosition.z})`);
        } else {
            // Default position if no spawn point is defined
            this.playerSpawnPosition = new THREE.Vector3(10, 5, 0);
            this.player.getObject().position.copy(this.playerSpawnPosition);
            console.log('Using default spawn position');
        }
        
        this.sceneManager.scene.add(this.player.getObject());

        // Load player model and initialize physics
        try {
            console.log('Loading unicycle model...');
            const modelLoaded = await this.player.loadModel('assets/models/GigaEinrad_3.glb');
            if (!modelLoaded) {
                console.warn('Failed to load unicycle model');
            } else {
                console.log('Unicycle model loaded successfully');
                // Initialize player physics and check the return value
                const physicsInitialized = this.player.initPhysics(this.physicsWorld);
                if (!physicsInitialized) {
                    console.error('Failed to initialize player physics');
                } else {
                    console.log('Player physics initialized successfully');
                }
            }
        } catch (error) {
            console.error('Error loading unicycle model:', error);
        }
        
        // Set game reference in PlayerUI for camera debug info
        if (this.player.playerUI) {
            this.player.playerUI.setGameReference(this);
        }

        // Initialize the start menu with the scene and renderer
        this.startMenu = new StartMenu();
        this.startMenu.init(this.sceneManager.scene, this.sceneManager.renderer);
        
        // Set up callbacks
        this.startMenu.onStartGame = () => {
            this.startGame();
        };
        
        this.startMenu.onResumeGame = () => {
            this.resumeGame();
        };
        
        this.startMenu.onRestartGame = () => {
            this.restartGame();
        };
        
        // Set up settings callback
        this.startMenu.onSettingChanged = (setting, value) => {
            this.applySetting(setting, value);
        };
        
        // Set up camera and controls
        this.sceneManager.camera.position.copy(this.cameraOffset);
        this.cameraControls = new OrbitControls(this.sceneManager.camera, this.sceneManager.renderer.domElement);
        this.cameraControls.enableDamping = true;
        this.cameraControls.dampingFactor = 0.05;
        this.cameraControls.minDistance = 2;
        this.cameraControls.maxDistance = 20;
        this.cameraControls.maxPolarAngle = Math.PI / 2;
        this.cameraControls.rotateSpeed = 0.75;
        
        // Listen for key to toggle camera mode (T key for toggle)
        document.addEventListener('keydown', (event) => {
            if (event.code === 'KeyT') {
                this.followPlayerDirection = !this.followPlayerDirection;
                console.log(`Camera mode: ${this.followPlayerDirection ? 'Follow direction' : 'Orbit controls'}`);
                
                // Configure camera constraints based on mode
                if (this.followPlayerDirection) {
                    // Direction-following mode: apply constraints
                    this.cameraControls.minDistance = 2;
                    this.cameraControls.maxDistance = 20;
                    this.cameraControls.maxPolarAngle = Math.PI / 2;
                    this.cameraControls.enableZoom = true;
                } else {
                    // Orbit mode: remove all constraints
                    this.cameraControls.minDistance = 0;
                    this.cameraControls.maxDistance = Infinity;
                    this.cameraControls.maxPolarAngle = Math.PI; // Allow full 180° rotation
                    this.cameraControls.minPolarAngle = 0;
                    this.cameraControls.enableZoom = true;
                }
            }
        });
        
        // Start animation loop
        this.animate();
    }

    startGame() {
        this.gameState = 'playing';
    }
    
    resumeGame() {
        this.gameState = 'playing';
    }
    
    restartGame() {
        // Reset player position
        this.resetPlayerPosition();
        // Change state to playing
        this.gameState = 'playing';
    }
    
    resetPlayerPosition() {
        if (this.player) {
            // Reset position
            this.player.getObject().position.copy(this.playerSpawnPosition);
            
            // Reset velocity
            if (this.player.rigidBody) {
                this.player.rigidBody.velocity.set(0, 0, 0);
                this.player.rigidBody.angularVelocity.set(0, 0, 0);
            }
            
            // Reset player state
            this.player.currentSpeed = 0;
            this.player.downhillSpeed = 0;
            this.player.jumpCharge = 0;
            this.player.isJumping = false;
            this.player.isCharging = false;
            
            // Reset rotation
            this.player.rotation.y = 0;
            
            // Make sure physics body position is updated
            if (this.player.rigidBody) {
                this.player.rigidBody.position.copy(this.playerSpawnPosition);
            }
        }
    }
    
    showMenu() {
        // Set state to paused if coming from playing, otherwise menu
        if (this.gameState === 'playing') {
            this.gameState = 'paused';
            // Update menu to show restart option
            this.startMenu.updateGameState('paused');
        } else {
            this.gameState = 'menu';
            this.startMenu.updateGameState('menu');
        }
        
        this.startMenu.toggleVisibility(true);
    }

    animate(currentTime = 0) {
        const deltaTime = (currentTime - this.lastTime) / 1000;
        this.lastTime = currentTime;

        this.update(deltaTime);
        this.render();

        requestAnimationFrame((time) => this.animate(time));
    }

    update(deltaTime) {
        // Handle input
        if (this.gameState === 'playing') {
            // Handle escape key to show menu
            if (this.inputManager.isKeyPressed('Escape')) {
                if (!this.wasEscapePressed) {
                    this.showMenu();
                    this.wasEscapePressed = true;
                }
            } else {
                this.wasEscapePressed = false;
            }
            
            // Update physics world
            if (this.physicsWorld) {
                this.physicsWorld.step(1/60, deltaTime, 3);
            }
            
            // Update player and camera
            if (this.player) {
                this.player.update(deltaTime, this.inputManager);
                
                // Update camera to follow player
                const playerPos = this.player.getObject().position;
                
                if (this.followPlayerDirection) {
                    // Get player rotation from Euler
                    const playerRotation = this.player.rotation.y;
                    
                    // Get player speed and calculate camera adjustment factor
                    const playerSpeed = Math.abs(this.player.currentSpeed) + Math.abs(this.player.downhillSpeed);
                    const speedFactor = Math.min(playerSpeed / this.maxSpeedForCamera, 1.0); // Clamp between 0 and 1
                    
                    // Calculate downhill boost factor (0 to 1) based on player's downhill speed
                    // Assumes player.maxDownhillBoost is the maximum possible downhill boost value
                    const downhillFactor = Math.min(this.player.downhillSpeed / this.player.maxDownhillBoost, 1.0);
                    
                    // Dynamically adjust camera offset based on speed
                    // For Y, combine regular speed factor with downhill factor
                    const maxYWithDownhill = this.maxCameraY + (this.maxCameraYWithDownhill - this.maxCameraY) * downhillFactor;
                    const dynamicCameraY = this.minCameraY + (maxYWithDownhill - this.minCameraY) * speedFactor;
                    const dynamicCameraZ = this.minCameraZ + (this.maxCameraZ - this.minCameraZ) * speedFactor;
                    
                    // Calculate camera position based on player's direction with dynamic offset
                    const cameraOffset = new THREE.Vector3(
                        Math.sin(playerRotation) * dynamicCameraZ,
                        dynamicCameraY,
                        Math.cos(playerRotation) * dynamicCameraZ
                    );
                    
                    // Position camera behind player
                    const idealPosition = playerPos.clone().sub(cameraOffset);
                    this.sceneManager.camera.position.lerp(idealPosition, this.cameraLerpFactor);
                    
                    // Set camera to look at player, but aim a bit lower to create a looking-down effect
                    const targetPosition = new THREE.Vector3(playerPos.x, playerPos.y, playerPos.z);
                    this.cameraControls.target.copy(targetPosition);
                } else {
                    // Original orbit camera behavior - without auto zoom reset
                    // Just update the camera target to follow player, but don't adjust position
                    const targetPosition = new THREE.Vector3(playerPos.x, playerPos.y + 1, playerPos.z);
                    this.cameraControls.target.lerp(targetPosition, this.cameraLerpFactor);
                }
            }
            
            // Update camera controls
            if (this.cameraControls) {
                this.cameraControls.update();
            }
        }
        
        // Update start menu if it's visible
        if (this.startMenu && this.startMenu.visible) {
            this.startMenu.updateMenuView(deltaTime);
        }
    }

    render() {
        if (this.startMenu && this.startMenu.visible && this.startMenu.getMenuCamera()) {
            // Use menu camera when menu is visible
            this.sceneManager.renderer.render(this.sceneManager.scene, this.startMenu.getMenuCamera());
        } else {
            // Use game camera during gameplay
            this.sceneManager.renderer.render(this.sceneManager.scene, this.sceneManager.camera);
        }
        
        // Update FPS counter
        if (this.fpsCounter) {
            this.fpsCounter.update();
        }
    }

    applySetting(setting, value) {
        // Handle different settings
        switch(setting) {
            case 'followPlayerDirection':
                this.followPlayerDirection = value;
                break;
            case 'showFpsCounter':
                if (this.fpsCounter) {
                    this.fpsCounter.setVisibility(value);
                }
                break;
            case 'showDebugVisuals':
                if (this.player && this.player.applySettings) {
                    this.player.applySettings({ showDebugVisuals: value });
                }
                break;
            default:
                console.log('Unknown setting:', setting);
        }
    }
}

const game = new Game();
game.init();